import { Box, Link, Tag, Text } from '@factorialco/deprecated-design-system'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import { PendingCardPayment as PendingCardPaymentType } from 'modules/banking/hooks/usePendingCardPayments'
import { last4digits } from 'modules/banking/lib/cards/last4digits'
import { cardShortTypeLabel } from 'modules/banking/lib/cards/shortTypeLabel'
import { getStatusLabel } from 'modules/expenses/lib/expensables/statusLabel'
import tagColor from 'modules/expenses/lib/tagColors'

import './index.styles.css'
interface Props {
  cardPayment: PendingCardPaymentType
}

export const PendingCardPayment = ({ cardPayment }: Props) => {
  const { navigation } = useNavigation()
  const expense_id = cardPayment.expense?.expensable.id
  const expensableUrl = expense_id
    ? navigation.expenses.list.expenses.expenseId.fullPath({
        expense_id,
      })
    : null
  const {
    access: { locale },
  } = useCurrentSession()

  const { merchantAmountCents, merchantCurrency } = cardPayment

  const merchantAmount = MoneyWithCurrency({
    optionalDecimals: true,
    amount: merchantAmountCents,
    currency: merchantCurrency,
    locale,
  })

  const amount = MoneyWithCurrency({
    optionalDecimals: true,
    amount: cardPayment.amountCents,
    currency: cardPayment.currency,
    locale,
  })

  return (
    <Box gap="s48">
      <Box>
        <Box gap="s16" paddingBottom="s16">
          <Text.Header2 weight="semibold">
            {i18n.t('banking.transactions.authorizations.pending_authorizations')}
          </Text.Header2>
        </Box>
        <Box className={'PendingCardPayment_grid'}>
          <Box flexDirection="column" gap="s4">
            <Text.Normal color="grey800" weight="semibold" size="200">
              {i18n.t('banking.transactions.authorizations.type')}
            </Text.Normal>
            <Tag inline>
              {cardShortTypeLabel(cardPayment.card)} {last4digits(cardPayment.card)}
            </Tag>
          </Box>
          {cardPayment.card.alias && (
            <Box flexDirection="column" gap="s4">
              <Text.Normal color="grey800" weight="semibold" size="200">
                {i18n.t('banking.transactions.authorizations.card_alias')}
              </Text.Normal>
              <Tag inline> {cardPayment.card.alias}</Tag>
            </Box>
          )}
          <Box flexDirection="column" gap="s4">
            <Text.Normal color="grey800" weight="semibold" size="200">
              {i18n.t('banking.transactions.authorizations.employee')}
            </Text.Normal>
            <Text.Normal color="grey1000" weight="medium" size="200">
              {cardPayment.card.cardholder.employee?.access?.fullName}
            </Text.Normal>
          </Box>
          <Box flexDirection="column" gap="s4">
            <Text.Normal color="grey800" weight="semibold" size="200">
              {i18n.t('banking.transactions.authorizations.transaction_date')}
            </Text.Normal>
            <Text.Normal color="grey1000" weight="medium" size="200">
              {dateBackendDateTimeToUI(cardPayment.externalCreatedAt)}
            </Text.Normal>
          </Box>
          <Box flexDirection="column" gap="s4">
            <Text.Normal color="grey800" weight="semibold" size="200">
              {i18n.t('banking.transactions.authorizations.merchant')}
            </Text.Normal>
            <Text.Normal color="grey1000" weight="medium" size="200">
              {cardPayment.expense?.merchantName}
            </Text.Normal>
          </Box>
          <Box flexDirection="column" gap="s4">
            <Text.Normal color="grey800" weight="semibold" size="200">
              {i18n.t('banking.transactions.authorizations.category')}
            </Text.Normal>
            <Text.Normal color="grey1000" weight="medium" size="200">
              {cardPayment.expense?.category?.name ??
                i18n.t('expenses_v2.expense.category.uncategorized')}
            </Text.Normal>
          </Box>
          <Box flexDirection="column" gap="s4">
            <Text.Normal color="grey800" weight="semibold" size="200">
              {i18n.t('banking.transactions.authorizations.original_amount')}
            </Text.Normal>
            <Text.Normal color="grey1000" weight="medium" size="200">
              {merchantAmount}
            </Text.Normal>
          </Box>
          <Box flexDirection="column" gap="s4">
            <Text.Normal color="grey800" weight="semibold" size="200">
              {i18n.t('banking.transactions.authorizations.amount')}
            </Text.Normal>
            <Text.Normal color="grey1000" weight="medium" size="200">
              {amount}
            </Text.Normal>
          </Box>
          {cardPayment.expense?.exchangeRate && (
            <Box flexDirection="column" gap="s4">
              <Text.Normal color="grey800" weight="semibold" size="200">
                {i18n.t('banking.transactions.authorizations.exchange_rate')}
              </Text.Normal>
              <Text.Normal color="grey1000" weight="medium" size="200">
                {cardPayment.expense?.exchangeRate}
              </Text.Normal>
            </Box>
          )}
        </Box>
      </Box>
      {!!cardPayment.expense && (
        <Box gap="s16">
          <Text.Header2 weight="semibold">
            {i18n.t('banking.transactions.authorizations.expense_information')}
          </Text.Header2>
          <Box className={'PendingCardPayment_expenseInfoGrid'}>
            {!!cardPayment.expense?.status && (
              <Box flexDirection="column" gap="s4">
                <Text.Normal color="grey800" weight="semibold" size="200">
                  {i18n.t('banking.transactions.authorizations.status')}
                </Text.Normal>
                <Tag
                  inline
                  showCircle
                  type={tagColor(cardPayment.expense?.status)}
                  leftItem="circle"
                >
                  {getStatusLabel(cardPayment.expense?.status)}
                </Tag>
              </Box>
            )}
            {!!cardPayment.expense?.reviewRequestAt && (
              <Box flexDirection="column" gap="s4">
                <Text.Normal color="grey800" weight="semibold" size="200">
                  {i18n.t('banking.transactions.authorizations.report_date')}
                </Text.Normal>
                <Tag inline>{dateBackendDateTimeToUI(cardPayment.expense?.reviewRequestAt)}</Tag>
              </Box>
            )}
          </Box>
          {expensableUrl && (
            <Box>
              <Link.Router to={expensableUrl}>
                {i18n.t('banking.transactions.authorizations.view_expense')}
              </Link.Router>
            </Box>
          )}
        </Box>
      )}
    </Box>
  )
}
